import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

const sessionKey: string = storageKeys.sessionId;

let updateInterval: NodeJS.Timeout | null = null;

interface SessionData {
  app_id: number;
  email: string;
  device: string;
}

const getSessionId = async (): Promise<string | undefined> => {
  return (await StorageHandler.load(sessionKey)) as string | undefined;
};
const setSessionId = (session_id: string) => {
  return StorageHandler.save(sessionKey, session_id, 'session');
};
const clearSessionId = () => {
  return StorageHandler.remove(sessionKey);
};

const startUpdateInterval = () => {
  if (updateInterval) clearInterval(updateInterval);

  updateInterval = setInterval(
    () => {
      void (async () => {
        const sessionId = await getSessionId();
        if (!sessionId) return;

        try {
          await APIHandler.updateSession(sessionId);
          console.info('Session updated');
        } catch (err) {
          console.error('Failed to update session:', err);
        }
      })();
    },
    5 * 60 * 1000,
  );
};

const stopUpdateInterval = () => {
  if (updateInterval) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
};

export const sessionManager = {
  start: async (_data: SessionData) => {
    const existingSession = await getSessionId();
    if (existingSession) return;

    try {
      const response = await APIHandler.startSession(_data);

      if (!response.session_id) throw new Error('No session ID returned from backend');
      setSessionId(response.session_id);
      startUpdateInterval(); // Start interval immediately after session starts
    } catch (err) {
      console.error('Failed to start session:', err);
      throw err;
    }
  },

  update: async () => {
    const sessionId = await getSessionId();
    if (!sessionId) {
      console.warn('No active session found to update');
      return;
    }

    try {
      await APIHandler.updateSession(sessionId);
      console.info('Session updated manually');
    } catch (err) {
      console.error('Failed to update session:', err);
    }
  },

  end: async () => {
    const sessionId = await getSessionId();
    if (!sessionId) {
      console.warn('No active session found to end');
      return;
    }

    try {
      await APIHandler.endSession(sessionId);
      clearSessionId();
      stopUpdateInterval();
      console.info('Session ended successfully:');
    } catch (err) {
      console.error('Failed to end session:', err);
      throw err;
    }
  },

  resumeSessionUpdates: async () => {
    const sessionId = await getSessionId();
    if (!sessionId) return;
    startUpdateInterval();
    console.info('Resumed session updates for session:', sessionId);
  },
};

export type SessionManager = typeof sessionManager;
